import LegalLayout, { LegalSection, LegalList, ContactBlock } from "@/pages/legal/LegalLayout";

const TOC = [
  { id: "what-are", label: "What are cookies?" },
  { id: "essential", label: "Essential cookies" },
  { id: "analytics", label: "Analytics & performance" },
  { id: "preference", label: "Preference cookies" },
  { id: "third-party", label: "Third-party services" },
  { id: "manage", label: "Managing cookies" },
  { id: "impact", label: "How choices affect the site" },
  { id: "contact", label: "Contact" },
];

export default function CookiePolicy() {
  return (
    <LegalLayout
      testid="cookies-page"
      title="Cookie Policy"
      intro="This Cookie Policy explains the small text files Relyn uses on our website and how you can control them."
      effectiveDate="December 1, 2026"
      lastUpdated="December 1, 2026"
      toc={TOC}
    >
      <LegalSection id="what-are" title="What are cookies?">
        <p>
          Cookies are small text files that a website stores on your device to remember
          information about your visit. Similar technologies (such as local storage,
          pixels, and SDKs) can perform the same function. In this policy, "cookies"
          refers to all of these technologies.
        </p>
        <p>
          We use cookies to keep the Relyn website working, remember your preferences,
          and understand how the site is used so we can improve it.
        </p>
      </LegalSection>

      <LegalSection id="essential" title="Essential cookies">
        <p>
          Essential cookies are strictly necessary for the website to work. They keep
          you signed in, remember your session, and protect against malicious activity.
          Without these cookies, core parts of the site will not function properly.
        </p>
        <LegalList
          items={[
            "Session and authentication state.",
            "Load balancing and infrastructure routing.",
            "Fraud prevention and security signals.",
          ]}
        />
      </LegalSection>

      <LegalSection id="analytics" title="Analytics and performance cookies">
        <p>
          Analytics cookies help us understand how visitors interact with Relyn so we
          can improve the experience. They collect aggregated, non-identifying
          information such as page views, referring pages, and feature usage.
        </p>
        <LegalList
          items={[
            "Product analytics (page views, feature usage, conversion funnels).",
            "Performance monitoring (load times, error rates).",
          ]}
        />
      </LegalSection>

      <LegalSection id="preference" title="Preference cookies">
        <p>
          Preference cookies remember choices you make on the site — for example, your
          preferred language, theme, or dismissed banners — so you don't have to set them
          again on every visit.
        </p>
      </LegalSection>

      <LegalSection id="third-party" title="Third-party services">
        <p>
          A small number of services we use may set their own cookies to help us operate
          Relyn. These include cloud infrastructure providers, analytics tools, and
          delivery networks. Each provider handles data in accordance with its own
          privacy policy and contractual obligations to Relyn.
        </p>
      </LegalSection>

      <LegalSection id="manage" title="Managing or disabling cookies">
        <p>You can control cookies in several ways:</p>
        <LegalList
          items={[
            "Adjust your browser settings to block or delete cookies. Most modern browsers let you view, manage, and delete cookies from settings under Privacy or Security.",
            "Use private or incognito browsing to prevent cookies from persisting between sessions.",
            "Opt out of specific analytics tools via the opt-out mechanisms those tools provide.",
          ]}
        />
      </LegalSection>

      <LegalSection id="impact" title="How your choices affect the website">
        <p>
          Blocking essential cookies may prevent parts of the site from working — for
          example, you may not be able to sign in or complete forms. Blocking analytics
          or preference cookies is safe: the site will still work, but we may not be
          able to remember your preferences or understand how to improve the experience.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          Questions about our cookie usage? Please reach us at{" "}
          <a href="mailto:support@relynapp.com" className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4">
            support@relynapp.com
          </a>
          .
        </p>
        <ContactBlock />
      </LegalSection>
    </LegalLayout>
  );
}
